import React, { useState, useEffect } from 'react';
import TeacherManagementService from '../../lib/services/teacherManagementService';
import './SubjectManagementModal.css';

const SubjectManagementModal = ({ 
  isOpen, 
  onClose, 
  subjectName, 
  currentTeachers, 
  onUpdate 
}) => {
  const [allTeachers, setAllTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [teacherRoles, setTeacherRoles] = useState({});
  const [managementService] = useState(new TeacherManagementService());

  const subjectRoles = [
    { value: '', label: '普通教師' },
    { value: '科主任', label: '科主任' },
    { value: '副科主任', label: '副科主任' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadAllTeachers();
      setSelectedTeachers(currentTeachers.map(t => t.id));
      // Initialize roles for current teachers
      const initialRoles = {};
      currentTeachers.forEach(teacher => {
        initialRoles[teacher.id] = teacher.role || '';
      });
      setTeacherRoles(initialRoles);
    }
  }, [isOpen, currentTeachers]);

  const loadAllTeachers = async () => {
    setLoading(true);
    try {
      const { teachers, error } = await managementService.getAllTeachers();
      if (error) {
        console.error('Error loading teachers:', error);
      } else {
        setAllTeachers(teachers);
      }
    } catch (error) {
      console.error('Error in loadAllTeachers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherToggle = (teacherId) => {
    setSelectedTeachers(prev => {
      if (prev.includes(teacherId)) {
        return prev.filter(id => id !== teacherId);
      } else {
        return [...prev, teacherId];
      }
    });
  };

  const handleRoleChange = (teacherId, newRole) => {
    setTeacherRoles(prev => ({
      ...prev,
      [teacherId]: newRole
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const currentTeacherIds = currentTeachers.map(t => t.id);
      
      // Remove teachers who are no longer selected
      for (const teacherId of currentTeacherIds) {
        if (!selectedTeachers.includes(teacherId)) {
          await managementService.removeTeacherFromSubject(teacherId, subjectName);
        }
      }

      // Add newly selected teachers and update existing ones
      for (const teacherId of selectedTeachers) {
        const role = teacherRoles[teacherId] || '';
        
        if (!currentTeacherIds.includes(teacherId)) {
          // Add new teacher
          await managementService.addTeacherToSubject(teacherId, subjectName, role);
        } else {
          // Update existing teacher's role if changed
          const currentTeacher = currentTeachers.find(t => t.id === teacherId);
          if (currentTeacher && currentTeacher.role !== role) {
            await managementService.updateTeacherSubjectRole(teacherId, subjectName, role);
          }
        }
      }

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>管理 {subjectName} 教師</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="loading">載入中...</div>
          ) : (
            <div className="teachers-list">
              <div className="list-header">
                <span>選擇教師 ({selectedTeachers.length} 位已選擇)</span>
              </div>
              <div className="teachers-grid">
                {allTeachers.map(teacher => (
                  <div 
                    key={teacher.id} 
                    className={`teacher-item ${selectedTeachers.includes(teacher.id) ? 'selected' : ''}`}
                  >
                    <div className="teacher-main" onClick={() => handleTeacherToggle(teacher.id)}>
                      <div className="teacher-avatar">
                        {teacher.name.charAt(0)}
                      </div>
                      <div className="teacher-info">
                        <div className="teacher-name">{teacher.name}</div>
                        <div className="teacher-specialty">{teacher.subject_specialty || '未指定'}</div>
                      </div>
                      <div className="selection-indicator">
                        {selectedTeachers.includes(teacher.id) ? '✓' : ''}
                      </div>
                    </div>
                    {selectedTeachers.includes(teacher.id) && (
                      <div className="role-selection">
                        <label>角色:</label>
                        <select 
                          value={teacherRoles[teacher.id] || ''} 
                          onChange={(e) => handleRoleChange(teacher.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {subjectRoles.map(role => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>
            取消
          </button>
          <button 
            className="save-button" 
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? '儲存中...' : '儲存變更'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubjectManagementModal;
